<script setup>
import {ref, computed, onMounted} from 'vue';
import axios from 'axios';
import {useRouter} from 'vue-router';
import {usePortalContentAndDesignStore} from '../../../stores/partners/portalContentAndDesignStore';

const portalContentAndDesignStore = usePortalContentAndDesignStore();
const router = useRouter();

const queue = ref([]);
const dismissing = ref(false);

const currentMessage = computed(() => queue.value[0] ?? null);
const translations = computed(() => portalContentAndDesignStore.translationSettings);

onMounted(() => {
    fetchPopupMessages();
});

const fetchPopupMessages = async () => {
    try {
        const response = await axios.get(route('partners_int.messages.popup'));
        if (response.data.status === 1) {
            queue.value = response.data.messages || [];
        }
    } catch (error) {
        console.error('Failed to fetch popup messages', error);
    }
};

const dismiss = async () => {
    const message = currentMessage.value;
    if (!message || dismissing.value) {
        return;
    }

    dismissing.value = true;
    try {
        await axios.post(route('partners_int.messages.dismiss-popup', {message: message.id}));
    } catch (error) {
        console.error('Failed to dismiss popup message', error);
    } finally {
        queue.value.shift();
        dismissing.value = false;
    }
};

const openMessages = async () => {
    await dismiss();
    // The inbox shows everything, so the rest of the queue must not stack on top of it.
    queue.value = [];
    router.push({name: 'messages'});
};
</script>

<template>
    <modal
        v-if="currentMessage"
        :key="currentMessage.id"
        :show="true"
        size="large"
        name="message_popup"
        :title="currentMessage.subject"
        :close-modal-on-outside-click="false"
        :show-cancel-button="false"
        @modalClose="dismiss"
    >
        <template #body>
            <div class="space-y-4 text-left">
                <div class="flex items-center gap-4 text-xs text-gray-500">
                    <span>{{ translations.portal_translation_messages_from ?? 'From:' }} {{ currentMessage.sender_name }}</span>
                    <span>{{ currentMessage.created_at_formatted }}</span>
                </div>

                <!-- Rich text authored by the program owner in the merchant app, not partner input -->
                <div class="text-sm text-gray-700 leading-relaxed message-body" v-html="currentMessage.body"></div>
            </div>
        </template>

        <template #footer>
            <btn
                type="light"
                :disabled="dismissing"
                @click="openMessages"
            >
                {{ translations.portal_translation_messages_view_messages ?? 'View messages' }}
            </btn>
            <btn
                type="primary"
                :disabled="dismissing"
                @click="dismiss"
            >
                {{ translations.portal_translation_messages_got_it ?? 'Got it' }}
            </btn>
        </template>
    </modal>
</template>

<style scoped>
/* Rich text content styles for TipTap editor output */
.message-body :deep(p) {
    margin-bottom: 0.75em;
    line-height: 1.625;
}

.message-body :deep(p:last-child) {
    margin-bottom: 0;
}

.message-body :deep(ul),
.message-body :deep(ol) {
    padding-left: 1.5rem;
    margin-bottom: 0.75em;
}

.message-body :deep(ul) {
    list-style-type: disc;
}

.message-body :deep(ol) {
    list-style-type: decimal;
}

.message-body :deep(li) {
    margin-bottom: 0.25em;
}

.message-body :deep(strong),
.message-body :deep(b) {
    font-weight: 600;
}

.message-body :deep(em),
.message-body :deep(i) {
    font-style: italic;
}

.message-body :deep(h1),
.message-body :deep(h2),
.message-body :deep(h3) {
    font-weight: 600;
    line-height: 1.3;
    margin-top: 0.875em;
    margin-bottom: 0.5em;
}

.message-body :deep(h1) {
    font-size: 1.5rem;
}

.message-body :deep(h2) {
    font-size: 1.25rem;
}

.message-body :deep(h3) {
    font-size: 1.125rem;
}

.message-body :deep(blockquote) {
    border-left: 4px solid #d1d5db;
    padding-left: 1rem;
    margin: 0.75em 0;
    font-style: italic;
    color: #6b7280;
}

.message-body :deep(a) {
    color: #0d9488;
    text-decoration: underline;
}

.message-body :deep(hr) {
    margin: 1.5em 0;
    border: none;
    height: 1px;
    background-color: #e5e7eb;
}
</style>
